import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Server, Database, Shield, Search, Info, Settings, Users, ArrowRight, CheckCircle, RefreshCw, AlertTriangle, Terminal, FileText, Play, Activity, Cpu, HardDrive, History, Undo, BrainCircuit } from 'lucide-react';

export default function ArchitecturePage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const { authAxios } = useAuth();
  
  // States für Live Metrics
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // States für Button-Simulation & Terminal
  const [actionStatus, setActionStatus] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [terminalOutput, setTerminalOutput] = useState('');

  // Time-Machine States
  const [snapshots, setSnapshots] = useState([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [diff, setDiff] = useState('');

  // AI State
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchAiAnalysis = async () => {
    setAiLoading(true);
    setAiAnalysis('');
    try {
      const api = authAxios();
      const res = await api.get('/system/ai-logs');
      setAiAnalysis(res.data.analysis);
    } catch (err) {
      setAiAnalysis('Fehler bei der KI-Analyse. Ist der API-Key gesetzt?');
    } finally {
      setAiLoading(false);
    }
  };

  const fetchSnapshots = async () => {
    setLoadingSnapshots(true);
    try {
      const api = authAxios();
      const res = await api.get('/system/timemachine');
      setSnapshots(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const createSnapshot = async () => {
    try {
      setActionStatus('loading');
      setActionMessage('Snapshot wird erstellt...');
      const api = authAxios();
      await api.post('/system/timemachine');
      fetchSnapshots();
      setActionStatus('success');
      setActionMessage('Snapshot erfolgreich erstellt.');
      setTimeout(() => setActionStatus(null), 3000);
    } catch (err) {
      setActionStatus('error');
      setActionMessage('Fehler beim Erstellen.');
      setTimeout(() => setActionStatus(null), 3000);
    }
  };

  const showDiff = async (hash) => {
    setSelectedSnapshot(hash);
    setDiff('Lade Änderungen...');
    try {
      const api = authAxios();
      const res = await api.get(`/system/timemachine/${hash}/diff`);
      setDiff(res.data.diff);
    } catch (err) {
      setDiff('Fehler beim Laden des Diffs.');
    }
  };

  const rollback = async (hash) => {
    if (!window.confirm('WARNUNG: Dies überschreibt die aktuelle Live-Konfiguration mit dem Snapshot. Fortfahren?')) return;
    try {
      setActionStatus('loading');
      setActionMessage(`Rollback auf ${hash.substring(0,7)} läuft...`);
      const api = authAxios();
      const res = await api.post(`/system/timemachine/${hash}/rollback`);
      setTerminalOutput(res.data.output || 'Rollback abgeschlossen.');
      setActionStatus('success');
      setActionMessage('Rollback erfolgreich!');
      fetchSnapshots();
    } catch (err) {
      setActionStatus('error');
      setActionMessage('Fehler beim Rollback.');
      setTerminalOutput(err.response?.data?.detail || err.message);
    }
  };

  const fetchMetrics = async () => {
    try {
      setLoadingMetrics(true);
      const api = authAxios();
      const res = await api.get('/system/metrics');
      setMetrics(res.data);
    } catch (err) {
      console.error('Error fetching metrics', err);
    } finally {
      setLoadingMetrics(false);
    }
  };

  useEffect(() => {
    let interval;
    if (activeTab === 'overview') {
      fetchMetrics();
      interval = setInterval(fetchMetrics, 5000); // refresh every 5 seconds
    }
    if (activeTab === 'timemachine') {
      fetchSnapshots();
    }
    return () => clearInterval(interval);
  }, [activeTab]);

  const executeAction = async (actionName, backendAction) => {
    setActionStatus('loading');
    setActionMessage(`${actionName} wird ausgeführt...`);
    setTerminalOutput('');
    
    try {
      const api = authAxios();
      const response = await api.post('/system/action', { action: backendAction });
      
      setActionStatus('success');
      setActionMessage(`${actionName} erfolgreich.`);
      setTerminalOutput(response.data.output || 'Keine Ausgabe.');
      
    } catch (error) {
      console.error(error);
      setActionStatus('error');
      setActionMessage(`Fehler bei ${actionName}.`);
      setTerminalOutput(error.response?.data?.detail || error.message || 'Ein unbekannter Fehler ist aufgetreten.');
    } finally {
      setTimeout(() => {
        setActionStatus(null);
      }, 5000);
    }
  };

  // Hilfsfunktion für die Suche
  const matches = (text) => {
    if (!searchQuery) return true;
    return text.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {matches('Architektur Routing Nginx Apache PHP') && (
              <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center justify-between">
                  <span>Umfassende Systemarchitektur (Fyreweb / FyreServe)</span>
                  {metrics && (
                    <div className="flex items-center gap-2 text-xs font-normal bg-green-500/10 text-green-400 px-3 py-1 rounded-full border border-green-500/20">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> Live Connected
                    </div>
                  )}
                </h3>
                <p className="text-[#a1a1aa] mb-6 leading-relaxed">
                  Unsere Server-Infrastruktur ist darauf ausgelegt, maximale Performance, Sicherheit und Ausfallsicherheit für unsere Kunden (Websites, Web-Apps, E-Mails) zu gewährleisten. 
                  Das Herzstück bildet unser dedizierter Server (IP: <strong>152.53.204.79</strong>), welcher mit dem Betriebssystem Debian 12 läuft und über HestiaCP verwaltet wird.
                </p>

                {/* Live Metrics Cards */}
                {metrics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-black/40 border border-white/5 p-4 rounded-lg flex flex-col items-center justify-center text-center relative overflow-hidden">
                      <div className="absolute top-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50"></div>
                      <Cpu className="w-5 h-5 text-blue-400 mb-2" />
                      <span className="text-2xl font-bold text-white">{metrics.cpu}%</span>
                      <span className="text-xs text-[#a1a1aa]">CPU Load</span>
                    </div>
                    <div className="bg-black/40 border border-white/5 p-4 rounded-lg flex flex-col items-center justify-center text-center relative overflow-hidden">
                      <div className="absolute top-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-50"></div>
                      <Database className="w-5 h-5 text-purple-400 mb-2" />
                      <span className="text-2xl font-bold text-white">{metrics.ram}%</span>
                      <span className="text-xs text-[#a1a1aa]">RAM Usage</span>
                    </div>
                    <div className="bg-black/40 border border-white/5 p-4 rounded-lg flex flex-col items-center justify-center text-center relative overflow-hidden">
                      <div className="absolute top-0 w-full h-1 bg-gradient-to-r from-transparent via-[#EF694C] to-transparent opacity-50"></div>
                      <HardDrive className="w-5 h-5 text-[#EF694C] mb-2" />
                      <span className="text-2xl font-bold text-white">{metrics.disk}%</span>
                      <span className="text-xs text-[#a1a1aa]">Disk (Root)</span>
                    </div>
                    <div className="bg-black/40 border border-white/5 p-4 rounded-lg flex flex-col items-center justify-center text-center relative overflow-hidden">
                      <div className="absolute top-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent opacity-50"></div>
                      <Activity className="w-5 h-5 text-green-400 mb-2" />
                      <span className="text-2xl font-bold text-white">{metrics.nginx}</span>
                      <span className="text-xs text-[#a1a1aa]">Active Web-Conns</span>
                    </div>
                  </div>
                )}
                
                <h4 className="text-white font-bold mb-2 mt-6">Interaktiver Datenfluss (Live-Traffic)</h4>
                <p className="text-[#a1a1aa] mb-4 leading-relaxed text-sm">
                  Die untenstehende Karte visualisiert den aktuellen Zustand des Servers. Die Verbindungen pulsieren basierend auf den Live-Metriken.
                </p>

                <div className="p-6 bg-black/40 rounded-lg border border-white/5 overflow-hidden relative">
                  {/* Visuelle Node-Map statt Text-Pre */}
                  <div className="flex flex-col items-center justify-center gap-6 py-4">
                    
                    {/* Internet Node */}
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center z-10 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                        <Search className="w-6 h-6 text-blue-400" />
                      </div>
                      <span className="text-xs font-bold text-white mt-2">Internet / User</span>
                    </div>

                    {/* Animated Line */}
                    <div className="h-10 w-px relative">
                      <div className="absolute inset-0 bg-gradient-to-b from-blue-500/50 to-green-500/50"></div>
                      {metrics && parseInt(metrics.nginx) > 0 && (
                        <div className="absolute top-0 left-1/2 w-2 h-2 -translate-x-1/2 bg-blue-400 rounded-full animate-[ping_1s_ease-in-out_infinite]"></div>
                      )}
                    </div>

                    {/* Nginx Gateway */}
                    <div className="w-full max-w-md bg-green-500/5 border border-green-500/20 rounded-xl p-4 flex flex-col items-center relative z-10">
                      <div className="absolute -top-3 px-3 py-0.5 bg-black border border-green-500/30 rounded-full text-[10px] text-green-400 font-bold uppercase tracking-wider">
                        Gateway (152.53.204.79)
                      </div>
                      <Server className="w-8 h-8 text-green-400 mb-2" />
                      <span className="font-bold text-white">Nginx Proxy & Firewall</span>
                      <span className="text-xs text-green-400/70">{metrics ? `${metrics.nginx} Live Connections` : 'Loading...'}</span>
                      
                      <div className="w-full flex justify-between mt-4 border-t border-white/5 pt-4">
                         <div className="flex flex-col items-center">
                           <Shield className="w-4 h-4 text-red-400 mb-1" />
                           <span className="text-[10px] text-[#a1a1aa]">Fail2Ban</span>
                         </div>
                         <div className="flex flex-col items-center">
                           <Info className="w-4 h-4 text-yellow-400 mb-1" />
                           <span className="text-[10px] text-[#a1a1aa]">SSL Term.</span>
                         </div>
                         <div className="flex flex-col items-center">
                           <RefreshCw className="w-4 h-4 text-blue-400 mb-1" />
                           <span className="text-[10px] text-[#a1a1aa]">Cache</span>
                         </div>
                      </div>
                    </div>

                    {/* Split Lines to Backends */}
                    <div className="w-full max-w-sm flex justify-between relative h-12">
                       {/* Left Line */}
                       <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-green-500/50 to-[#EF694C]/50">
                         {metrics && parseInt(metrics.cpu) > 5 && (
                           <div className="absolute top-0 left-1/2 w-1.5 h-1.5 -translate-x-1/2 bg-[#EF694C] rounded-full animate-[ping_1.5s_ease-in-out_infinite]"></div>
                         )}
                       </div>
                       {/* Right Line */}
                       <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-green-500/50 to-purple-500/50">
                         {metrics && parseInt(metrics.ram) > 20 && (
                           <div className="absolute top-0 left-1/2 w-1.5 h-1.5 -translate-x-1/2 bg-purple-400 rounded-full animate-[ping_2s_ease-in-out_infinite]"></div>
                         )}
                       </div>
                    </div>

                    {/* Backends */}
                    <div className="w-full max-w-md flex gap-4 relative z-10">
                      <div className="flex-1 bg-[#EF694C]/5 border border-[#EF694C]/20 rounded-xl p-4 flex flex-col items-center text-center">
                        <Activity className="w-6 h-6 text-[#EF694C] mb-2" />
                        <span className="font-bold text-white text-sm">Apache / PHP-FPM</span>
                        <span className="text-[10px] text-[#a1a1aa] mt-1">WordPress & Apps</span>
                      </div>
                      <div className="flex-1 bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 flex flex-col items-center text-center">
                        <Database className="w-6 h-6 text-purple-400 mb-2" />
                        <span className="font-bold text-white text-sm">MariaDB</span>
                        <span className="text-[10px] text-[#a1a1aa] mt-1">Datenbanken</span>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'ailogs':
        return (
          <div className="space-y-6">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <BrainCircuit className="w-5 h-5 text-purple-400" /> KI-gestützte Log-Analyse
                  </h3>
                  <p className="text-[#a1a1aa] mt-1 text-sm">
                    Lass die letzten Server-Logs automatisch von der KI auf Fehler analysieren und dir Empfehlungen in natürlicher Sprache geben.
                  </p>
                </div>
                <button 
                  onClick={fetchAiAnalysis}
                  disabled={aiLoading}
                  className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Logs jetzt analysieren
                </button>
              </div>

              {aiLoading && (
                <div className="p-8 flex flex-col items-center justify-center text-purple-400 gap-4 border border-purple-500/20 bg-purple-500/5 rounded-xl">
                  <BrainCircuit className="w-8 h-8 animate-pulse" />
                  <span>KI liest und analysiert die Server-Logs...</span>
                </div>
              )}

              {aiAnalysis && !aiLoading && (
                <div className="p-6 border border-white/10 bg-black/40 rounded-xl">
                  <h4 className="font-bold text-white mb-4">KI Diagnosebericht</h4>
                  <div className="text-[#a1a1aa] whitespace-pre-wrap leading-relaxed">
                    {aiAnalysis}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 'timemachine':
        return (
          <div className="space-y-6">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-blue-400" /> Time-Machine (GitOps)
                  </h3>
                  <p className="text-[#a1a1aa] mt-1 text-sm">
                    Versionierung von /etc/nginx, /usr/local/hestia/conf und /etc/fail2ban. 
                  </p>
                </div>
                <button 
                  onClick={createSnapshot}
                  className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Manuellen Snapshot erstellen
                </button>
              </div>

              {actionStatus && (
                 <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                   actionStatus === 'loading' ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400' :
                   actionStatus === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400' :
                   'bg-red-500/10 border border-red-500/30 text-red-400'
                 }`}>
                   {actionStatus === 'loading' && <RefreshCw className="w-5 h-5 animate-spin" />}
                   {actionStatus === 'success' && <CheckCircle className="w-5 h-5" />}
                   {actionStatus === 'error' && <AlertTriangle className="w-5 h-5" />}
                   <span className="text-sm font-medium">{actionMessage}</span>
                 </div>
              )}

              {terminalOutput && (
                 <div className="bg-black p-4 rounded-lg border border-white/10 font-mono text-sm text-green-400 whitespace-pre-wrap max-h-64 overflow-y-auto mb-6">
                   {terminalOutput}
                 </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 border border-white/5 rounded-lg overflow-hidden bg-black/20">
                  <div className="bg-white/5 p-3 border-b border-white/5 font-medium text-sm text-white">Letzte Snapshots</div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {loadingSnapshots ? (
                      <div className="p-4 text-center text-[#a1a1aa] text-sm flex justify-center"><RefreshCw className="w-4 h-4 animate-spin" /></div>
                    ) : snapshots.length === 0 ? (
                      <div className="p-4 text-center text-[#a1a1aa] text-sm">Keine Snapshots gefunden.</div>
                    ) : (
                      snapshots.map(s => (
                        <div 
                          key={s.hash}
                          onClick={() => showDiff(s.hash)}
                          className={`p-3 border-b border-white/5 cursor-pointer transition-colors ${selectedSnapshot === s.hash ? 'bg-blue-500/10 border-l-2 border-l-blue-500' : 'hover:bg-white/5'}`}
                        >
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-mono text-blue-400">{s.hash.substring(0,7)}</span>
                            <span className="text-xs text-[#a1a1aa]">{new Date(s.date).toLocaleString()}</span>
                          </div>
                          <div className="text-sm text-white truncate">{s.message}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="lg:col-span-2 border border-white/5 rounded-lg overflow-hidden bg-black/20 flex flex-col">
                  <div className="bg-white/5 p-3 border-b border-white/5 flex justify-between items-center">
                    <span className="font-medium text-sm text-white">
                      {selectedSnapshot ? `Änderungen in Snapshot ${selectedSnapshot.substring(0,7)}` : 'Wähle einen Snapshot'}
                    </span>
                    {selectedSnapshot && (
                      <button 
                        onClick={() => rollback(selectedSnapshot)}
                        className="flex items-center gap-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 px-3 py-1 rounded transition-colors"
                      >
                        <Undo className="w-3 h-3" /> Rollback hierhin
                      </button>
                    )}
                  </div>
                  <div className="p-4 overflow-y-auto max-h-[400px]">
                    {selectedSnapshot ? (
                      <pre className="text-xs font-mono text-[#a1a1aa] whitespace-pre-wrap">
                        {diff.split('\n').map((line, i) => (
                          <div key={i} className={
                            line.startsWith('+') && !line.startsWith('+++') ? 'text-green-400 bg-green-500/10' :
                            line.startsWith('-') && !line.startsWith('---') ? 'text-red-400 bg-red-500/10' : ''
                          }>
                            {line}
                          </div>
                        ))}
                      </pre>
                    ) : (
                      <div className="text-center text-[#a1a1aa] text-sm mt-10">Klicke links auf einen Snapshot, um die Änderungen zu sehen.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'docs':
        return (
          <div className="space-y-4">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-6">Detaillierte Dokumentation & Spezifikationen</h3>
              
              <div className="space-y-6">
                {matches('Betriebssystem Panel Debian HestiaCP') && (
                  <div>
                    <h4 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
                      <Server className="w-5 h-5 text-[#EF694C]" /> 1. Betriebssystem & Panel
                    </h4>
                    <p className="text-[#a1a1aa] pl-7">
                      Wir nutzen <strong>Debian 12 (Bookworm)</strong> aufgrund seiner hohen Stabilität. 
                      Als Control Panel setzen wir <strong>HestiaCP</strong> ein. Es ist ressourcenschonend, Open-Source und bietet uns eine API sowie ein Web-Interface.
                    </p>
                  </div>
                )}

                {matches('Sicherheit Firewall Fail2Ban UFW') && (
                  <div>
                    <h4 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#EF694C]" /> 2. Sicherheitskonzept (Firewall & Fail2Ban)
                    </h4>
                    <p className="text-[#a1a1aa] pl-7">
                      Das System ist durch <strong>UFW (Uncomplicated Firewall)</strong> geschützt. Es sind nur zwingend notwendige Ports von außen erreichbar.<br/><br/>
                      Zusätzlich läuft <strong>Fail2Ban</strong>. Dieser Dienst analysiert in Echtzeit die Log-Dateien des Servers. Sobald ein Angreifer zu oft ein falsches Passwort eingibt, wird die IP gesperrt.
                    </p>
                  </div>
                )}

                {matches('Datenbanken Backups MariaDB MySQL') && (
                  <div>
                    <h4 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
                      <Database className="w-5 h-5 text-[#EF694C]" /> 3. Datenbanken & Backups
                    </h4>
                    <p className="text-[#a1a1aa] pl-7">
                      Die Kunden-Datenbanken laufen auf <strong>MariaDB</strong>. Tägliche Backups der Datenbanken und Web-Verzeichnisse werden durch HestiaCP erstellt.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'config':
        return (
          <div className="space-y-6">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
               <h3 className="text-xl font-bold text-white mb-6">Konfiguration & Admin-Tools</h3>
               
               <p className="text-[#a1a1aa] mb-6">
                 Hier können Sie direkt auf die wichtigsten Konfigurationen zugreifen und administrative Befehle ausführen, ohne SSH nutzen zu müssen.
               </p>

               <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-8">
                 {matches('HestiaCP Core Config /usr/local/hestia/conf/') && (
                   <div className="p-4 bg-white/5 rounded-lg border border-white/10 flex flex-col justify-between">
                     <div>
                       <h4 className="font-bold text-white mb-2">HestiaCP Core Config</h4>
                       <code className="text-xs text-[#EF694C] block mb-2 bg-black/30 p-2 rounded">/usr/local/hestia/conf/</code>
                       <p className="text-sm text-[#a1a1aa] mb-4">Zentrale Einstellungen für das Panel und Benutzer.</p>
                     </div>
                     <button 
                       onClick={() => executeAction('Hestia Config lesen', 'read_hestia_config')}
                       className="flex items-center justify-center gap-2 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] py-2 rounded text-sm transition-colors w-full"
                     >
                       <FileText className="w-4 h-4" /> Config anzeigen
                     </button>
                   </div>
                 )}
                 
                 {matches('Nginx Reverse Proxy /etc/nginx/nginx.conf /home/user/conf/web/') && (
                   <div className="p-4 bg-white/5 rounded-lg border border-white/10 flex flex-col justify-between">
                     <div>
                       <h4 className="font-bold text-white mb-2">Nginx Reverse Proxy</h4>
                       <code className="text-xs text-[#EF694C] block mb-2 bg-black/30 p-2 rounded">/etc/nginx/nginx.conf</code>
                       <p className="text-sm text-[#a1a1aa] mb-4">Globale Nginx-Settings und Routing.</p>
                     </div>
                     <div className="flex gap-2">
                       <button 
                         onClick={() => executeAction('Nginx Config lesen', 'read_nginx_config')}
                         className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-2 rounded text-sm transition-colors"
                       >
                         <FileText className="w-4 h-4" /> Anzeigen
                       </button>
                       <button 
                         onClick={() => executeAction('Nginx Reload', 'reload_nginx')}
                         className="flex-1 flex items-center justify-center gap-2 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] py-2 rounded text-sm transition-colors"
                       >
                         <RefreshCw className="w-4 h-4" /> Reload
                       </button>
                     </div>
                   </div>
                 )}

                 {matches('Fail2Ban Filter Jails /etc/fail2ban/jail.local') && (
                   <div className="p-4 bg-white/5 rounded-lg border border-white/10 flex flex-col justify-between">
                     <div>
                       <h4 className="font-bold text-white mb-2">Fail2Ban Jails</h4>
                       <code className="text-xs text-[#EF694C] block mb-2 bg-black/30 p-2 rounded">/etc/fail2ban/jail.local</code>
                       <p className="text-sm text-[#a1a1aa] mb-4">Definition der aktiven Jails und Bann-Zeiten.</p>
                     </div>
                     <div className="flex gap-2">
                       <button 
                         onClick={() => executeAction('Jail Config lesen', 'read_jail_config')}
                         className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-2 rounded text-sm transition-colors"
                       >
                         <FileText className="w-4 h-4" /> Anzeigen
                       </button>
                       <button 
                         onClick={() => executeAction('Fail2Ban Status', 'fail2ban_status')}
                         className="flex-1 flex items-center justify-center gap-2 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] py-2 rounded text-sm transition-colors"
                       >
                         <Shield className="w-4 h-4" /> Status
                       </button>
                     </div>
                   </div>
                 )}

                 {matches('PHP-FPM Pools /etc/php/8.x/fpm/pool.d/') && (
                   <div className="p-4 bg-white/5 rounded-lg border border-white/10 flex flex-col justify-between">
                     <div>
                       <h4 className="font-bold text-white mb-2">PHP-FPM Pools</h4>
                       <code className="text-xs text-[#EF694C] block mb-2 bg-black/30 p-2 rounded">/etc/php/8.2/fpm/pool.d/</code>
                       <p className="text-sm text-[#a1a1aa] mb-4">Kunden-Pools für Arbeitsspeicher und Worker-Prozesse.</p>
                     </div>
                     <button 
                       onClick={() => executeAction('Liste PHP Pools', 'list_php_pools')}
                       className="flex items-center justify-center gap-2 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] py-2 rounded text-sm transition-colors w-full"
                     >
                       <Terminal className="w-4 h-4" /> Pools auflisten
                     </button>
                   </div>
                 )}
               </div>
               
               {/* Global Terminal / Status */}
               {actionStatus && (
                 <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                   actionStatus === 'loading' ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400' :
                   actionStatus === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400' :
                   'bg-red-500/10 border border-red-500/30 text-red-400'
                 }`}>
                   {actionStatus === 'loading' && <RefreshCw className="w-5 h-5 animate-spin" />}
                   {actionStatus === 'success' && <CheckCircle className="w-5 h-5" />}
                   {actionStatus === 'error' && <AlertTriangle className="w-5 h-5" />}
                   <span className="text-sm font-medium">{actionMessage}</span>
                 </div>
               )}

               {terminalOutput && (
                 <div className="bg-black p-4 rounded-lg border border-white/10 font-mono text-sm text-green-400 whitespace-pre-wrap max-h-64 overflow-y-auto mt-4">
                   {terminalOutput}
                 </div>
               )}

            </div>
          </div>
        );
      case 'contacts':
        return (
          <div className="space-y-6">
            <div className="bg-[#121214] border border-white/10 rounded-xl p-6">
               <h3 className="text-xl font-bold text-white mb-6">Verantwortlichkeiten & Eskalationspfade</h3>
               <p className="text-[#a1a1aa] mb-8">
                 Um bei Ausfällen oder Störungen schnell reagieren zu können, ist eine klare Aufgabentrennung notwendig. 
               </p>
               
               <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/10 before:to-transparent">
                 
                 {matches('Level 1 First-Level-Support Fyreweb') && (
                 <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                   <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#121214] bg-green-500 text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_10px_rgba(34,197,94,0.4)] z-10">
                     1
                   </div>
                   <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-5 rounded-xl border border-green-500/30 bg-green-500/5">
                     <h4 className="font-bold text-white text-lg mb-2">First-Level-Support (Fyreweb)</h4>
                     <p className="text-sm text-[#a1a1aa] mb-2"><strong>Zuständigkeit:</strong> Allgemeine Kundenanfragen, Passwort-Resets, einfache Webseiten-Fehler, DNS-Änderungen.</p>
                     <p className="text-sm text-green-400"><strong>Aktion:</strong> Lösung direkt über HestiaCP oder das FyreServe Dashboard.</p>
                   </div>
                 </div>
                 )}

                 {matches('Level 2 Systemadministration SSH') && (
                 <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                   <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#121214] bg-yellow-500 text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_10px_rgba(234,179,8,0.4)] z-10">
                     2
                   </div>
                   <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-5 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
                     <h4 className="font-bold text-white text-lg mb-2">Systemadministration</h4>
                     <p className="text-sm text-[#a1a1aa] mb-2"><strong>Zuständigkeit:</strong> Server-Ausfälle (Error 502/504), Dienst-Abstürze, komplexe Routing- oder Firewall-Probleme.</p>
                     <p className="text-sm text-yellow-400"><strong>Aktion:</strong> Fehleranalyse über Log-Auswertung. Neustart von Diensten oder Anpassung der Konfigurationen.</p>
                   </div>
                 </div>
                 )}

                 {matches('Level 3 Hosting-Provider Netcup') && (
                 <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                   <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#121214] bg-red-500 text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_10px_rgba(239,68,68,0.4)] z-10">
                     3
                   </div>
                   <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] p-5 rounded-xl border border-red-500/30 bg-red-500/5">
                     <h4 className="font-bold text-white text-lg mb-2">Hosting-Provider (Netcup)</h4>
                     <p className="text-sm text-[#a1a1aa] mb-2"><strong>Zuständigkeit:</strong> Hardware-Defekte, Netzwerk-Ausfälle, IP-Blacklisting auf Backbone-Ebene.</p>
                     <p className="text-sm text-red-400"><strong>Aktion:</strong> Eröffnung eines Support-Tickets über das Netcup CCP.</p>
                   </div>
                 </div>
                 )}
               </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-white tracking-tight">Server Infrastruktur & Setup</h1>
          <p className="text-[#a1a1aa] mt-2">Detaillierte Übersicht unserer Hosting-Architektur, Dokumentation und Verwaltung (v1.3)</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a1a1aa]" />
          <input 
            type="text"
            placeholder="Dokumentation durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 bg-[#121214] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#EF694C] w-64 transition-colors"
          />
        </div>
      </div>

      <div className="flex overflow-x-auto border-b border-white/10 no-scrollbar">
        {[
          { id: 'overview', label: 'Architektur & Diagramm', icon: Server },
          { id: 'timemachine', label: 'Time-Machine', icon: History },
          { id: 'ailogs', label: 'KI Log-Analyse', icon: BrainCircuit },
          { id: 'docs', label: 'Dokumentation', icon: Info },
          { id: 'config', label: 'Konfiguration & Admin', icon: Settings },
          { id: 'contacts', label: 'Eskalationsmatrix', icon: Users }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
              className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-[#EF694C] text-[#EF694C]' 
                  : 'border-transparent text-[#a1a1aa] hover:text-white hover:border-white/20'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-[500px]">
        {renderContent()}
      </div>
    </div>
  );
}
